<template>
  <v-card
    class="px-3 question-control"
    width="100%"
    outlined
    elevation="2"
  >
    <v-row
      class="py-3"
      align="center"
    >
      <v-col
        cols="7"
        sm="10"
        md="10"
        lg="10"
        xl="10"
      >
        <div class="d-flex justify-sart align-center">
          <v-icon
            v-if="!isInEditMode"
            class="mr-3 control-drag-icon"
          >
            mdi-drag
          </v-icon>
          <ScreeningQuestionIcon
            class="mr-3"
            :type="question.type"
          />
          <span v-if="!isInEditMode">{{ question.body }}</span>
          <span v-else>{{ label }}</span>
        </div>
      </v-col>

      <!-- edit/close icons -->
      <v-col
        cols="5"
        sm="2"
        md="2"
        lg="2"
        xl="2"
        class="text-right"
      >
        <v-btn
          v-if="!isInEditMode"
          fab
          dark
          x-small
          color="secondary"
          title="Edit question"
          @click="toggleEditMode(true)"
        >
          <v-icon dark>
            edit
          </v-icon>
        </v-btn>
        <v-btn
          v-else
          fab
          dark
          x-small
          color="grey"
          title="Close editing"
          @click="toggleEditMode(false)"
        >
          <v-icon
            dark
          >
            close
          </v-icon>
        </v-btn>
        <v-btn
          class="ml-2"
          fab
          dark
          x-small
          color="appRed"
          title="Delete question"
          @click="handleQuestionDeleteClick"
        >
          <v-icon
            dark
          >
            delete_forever
          </v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <!-- body input -->
    <div v-if="!!isInEditMode">
      <v-divider />
      <v-row class="py-3">
        <v-col cols="12">
          <v-text-field
            ref="bodyInputEl"
            v-model="question.body"
            required
            solo
            color="primary"
            placeholder="Type a question.."
            :rules="rules.requiredStringRule"
          />
        </v-col>
        <v-col
          v-if="question.type === 'file'"
          cols="12"
        >
          <v-autocomplete
            solo
            dense
            multiple
            placeholder="Select file extensions available for upload"
            v-model="question.extensions"
            :rules="rules.requiredStringRule"
            :items="extensionsList"
          />
          <span class="body-2 grey--text">
            If you didn't find your file extension, please contact us at
            <a
              class="link"
              href="mailto:info@riverflex.com"
            >
              info@riverflex.com
            </a>
          </span>
        </v-col>
      </v-row>

      <!-- answers -->
      <v-card
        class="pa-4 pt-0 answer-options"
        v-if="question.answerOptions"
      >
        <v-row>
          <v-col cols="12">
            <h3>Answers</h3>
          </v-col>
          <draggable
            class="col col-12"
            v-model="question.answerOptions"
            handle=".mdi-drag"
            group="answerOptions"
          >
            <v-col
              v-for="(option, index) in question.answerOptions"
              class="pb-0"
              cols="12"
              :key="option.order"
            >
              <v-text-field
                ref="answerOptionInputEls"
                v-model="option.body"
                required
                solo
                color="primary"
                placeholder="Add a choice.."
                prepend-icon="mdi-drag"
                :rules="rules.requiredStringRule"
                @keyup.enter="handleAnswerAddClick"
              >
                <template v-slot:append>
                  <v-icon
                    v-if="canDeleteAnswerOption"
                    @click="handleAnswerOptionDelete(index)"
                  >
                    close
                  </v-icon>
                </template>
              </v-text-field>
            </v-col>
          </draggable>
          <v-col
            class="text-center"
            cols="12"
          >
            <v-btn
              color="primary"
              rounded
              dark
              small
              @click="handleAnswerAddClick"
            >
              Add
            </v-btn>
          </v-col>
        </v-row>
      </v-card>

      <!-- action buttons -->
      <v-divider />
      <v-row class="py-3">
        <v-col>
          <v-btn
            color="appRed"
            rounded
            dark
            small
            @click="handleQuestionDeleteClick"
          >
            Delete
          </v-btn>
        </v-col>
        <v-col cols="auto">
          <v-btn
            rounded
            color="grey"
            dark
            small
            @click="toggleEditMode(false)"
          >
            Close
          </v-btn>
        </v-col>
      </v-row>
    </div>
  </v-card>
</template>

<script lang="ts">
import { Component, Prop, Vue, Mixins } from 'vue-property-decorator'
import draggable from 'vuedraggable'

import { IScreeningQuestion, ScreeningQuestionType, IAnswerOption, IControlItem } from '@/types/models/screeningQuestion.model'
import ScreeningQuestionIcon from '@/components/screeningQuestions/ScreeningQuestionIcon.vue'
import extensionsList from '@/assets/screening-question-file-extensions-list'
import { PartialBy } from '@/types/utils'
import { waitAsync } from '@/utils/common'
import { IOption } from '@/types/form'

@Component({
  components: {
    draggable,
    ScreeningQuestionIcon
  }
})
export default class ScreeningQuestionControl extends Vue {
  @Prop({ required: true }) question: IControlItem
  @Prop({ required: true }) label: string

  isInEditMode: boolean = false
  formModel: boolean = false
  extensionsList: IOption[] = extensionsList.map(item => ({
    text: item,
    value: item,
    divider: item === '-'
  }))
  rules = {
    requiredStringRule: [
      (value: string) => !!value || 'This field is required!',
    ]
  }

  $refs: {
    bodyInputEl: HTMLInputElement
    answerOptionInputEls: HTMLInputElement[]
  }

  get canDeleteAnswerOption (): boolean {
    if (!this.question.answerOptions) return false
    return this.question.answerOptions.length > 1
  }

  public focus () {
    this.toggleEditMode(true)
  }

  private async toggleEditMode (toggle: boolean) {
    this.isInEditMode = toggle

    if (this.isInEditMode) {
      await waitAsync(100)
      this.$refs.bodyInputEl.focus()
    }
  }

  private createEmptyAnswerOption (order: number = 0): IAnswerOption {
    return { body: '', order }
  }

  private handleQuestionDeleteClick () {
    this.$emit('delete')
  }

  private handleAnswerOptionDelete (index: number) {
    this.question.answerOptions?.splice(index, 1)
  }

  private async handleAnswerAddClick () {
    if (!this.question.answerOptions) {
      this.question.answerOptions = [this.createEmptyAnswerOption()]
    }

    const maxOrder = !this.question.answerOptions.length ? 0 : Math.max(...this.question.answerOptions.map(item => item.order))
    this.question.answerOptions = [
      ...this.question.answerOptions,
      this.createEmptyAnswerOption(maxOrder + 1)
    ]

    await waitAsync(100)
    const lastInputEl = this.$refs.answerOptionInputEls[this.$refs.answerOptionInputEls.length - 1]
    lastInputEl?.focus()
  }
}
</script>
<style lang="scss" scoped>
.control-drag-icon,
.answer-options ::v-deep .mdi-drag {
  cursor: move;
}
.question-control ::v-deep .v-text-field__details {
  margin-bottom: 0;
}
</style>
